import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiInfo, FiX, FiLightbulb, FiTarget, FiZap } = FiIcons;

const Tooltip = ({ 
  children, 
  content, 
  title, 
  position = 'top', 
  variant = 'default',
  showDelay = 500,
  hideDelay = 200,
  persistent = false,
  icon = null,
  className = '' 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);

  const variants = {
    default: {
      bg: 'bg-gradient-to-br from-shadow-900/95 to-shadow-800/95',
      border: 'border-forge-400/30',
      text: 'text-forge-200',
      titleText: 'text-forge-400'
    },
    success: {
      bg: 'bg-gradient-to-br from-emerald-900/95 to-green-900/95',
      border: 'border-emerald-400/30',
      text: 'text-emerald-200',
      titleText: 'text-emerald-400'
    },
    warning: {
      bg: 'bg-gradient-to-br from-orange-900/95 to-red-900/95',
      border: 'border-orange-400/30',
      text: 'text-orange-200',
      titleText: 'text-orange-400'
    },
    info: {
      bg: 'bg-gradient-to-br from-blue-900/95 to-indigo-900/95',
      border: 'border-blue-400/30',
      text: 'text-blue-200',
      titleText: 'text-blue-400'
    }
  };

  const variantStyles = variants[variant] || variants.default;

  const getPositionClasses = () => {
    switch (position) {
      case 'top':
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
      case 'bottom':
        return 'top-full left-1/2 transform -translate-x-1/2 mt-2';
      case 'left':
        return 'right-full top-1/2 transform -translate-y-1/2 mr-2';
      case 'right':
        return 'left-full top-1/2 transform -translate-y-1/2 ml-2';
      default:
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
    }
  };

  const getArrowClasses = () => {
    const baseClasses = `absolute w-2 h-2 ${variantStyles.bg} ${variantStyles.border} transform rotate-45`;
    
    switch (position) {
      case 'top':
        return `${baseClasses} top-full left-1/2 -translate-x-1/2 -mt-1`;
      case 'bottom':
        return `${baseClasses} bottom-full left-1/2 -translate-x-1/2 -mb-1`;
      case 'left':
        return `${baseClasses} left-full top-1/2 -translate-y-1/2 -ml-1`;
      case 'right':
        return `${baseClasses} right-full top-1/2 -translate-y-1/2 -mr-1`;
      default:
        return `${baseClasses} top-full left-1/2 -translate-x-1/2 -mt-1`;
    }
  };

  const showTooltip = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    const id = setTimeout(() => {
      setIsVisible(true);
    }, showDelay);
    setTimeoutId(id);
  };

  const hideTooltip = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (!persistent) {
      const id = setTimeout(() => {
        setIsVisible(false);
      }, hideDelay);
      setTimeoutId(id);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  const getVariantIcon = () => {
    if (icon) return icon;
    
    switch (variant) {
      case 'success':
        return FiTarget;
      case 'warning':
        return FiZap;
      case 'info':
        return FiLightbulb;
      default:
        return FiInfo;
    }
  };

  const TooltipIcon = getVariantIcon();

  return (
    <div 
      className={`relative inline-block ${className}`}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {children}
      
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={`absolute z-50 ${getPositionClasses()}`}
          >
            <div className={`${variantStyles.bg} backdrop-blur-sm ${variantStyles.border} border rounded-xl shadow-dominance p-3 max-w-xs`}>
              <div className={getArrowClasses()} />
              
              {title && (
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={TooltipIcon} className={`${variantStyles.titleText} text-sm`} />
                    <h4 className={`font-semibold text-sm ${variantStyles.titleText}`}>
                      {title}
                    </h4>
                  </div>
                  {persistent && (
                    <button
                      onClick={handleClose}
                      className={`p-1 ${variantStyles.text} hover:${variantStyles.titleText} transition-colors`}
                    >
                      <SafeIcon icon={FiX} className="text-xs" />
                    </button>
                  )}
                </div>
              )}
              
              <div className={`text-sm ${variantStyles.text}`}>
                {content}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Tooltip;